import { parseJson, stringifyJson } from '../../../services/Utilities';

/**
 * Masters Service
 *
 * Keeps track of the current navigation state of the masters list.
 * When the user returns to the masters list, it restores the last saved state.
 * Uses sessionStorage for persistence across component disposal and page navigation.
 *
 * Also provides API calls for master/template operations.
 */

const API_SERVER = process.env.API_SERVER || '';

export interface MasterAssignmentResponse {
  name: string;
  description?: string;
  notification?: string;
  is_guided?: boolean;
  project_id?: number;
  [key: string]: unknown;
}

export interface BreadcrumbItem {
  id: number | string | null;
  name: string;
}

export interface MastersState {
  breadcrumbs: BreadcrumbItem[];
}

const STORAGE_KEY = 'masters_navigation_state';

const DEFAULT_STATE: MastersState = {
  breadcrumbs: [{ id: null, name: 'Library' }],
};

/**
 * Saves the current masters navigation state to sessionStorage.
 * @param state - The state to save
 */
export function saveState(state: MastersState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, stringifyJson({
      breadcrumbs: state.breadcrumbs,
    }));
  } catch {
    // sessionStorage may not be available
  }
}

/**
 * Gets the saved masters navigation state from sessionStorage.
 * @returns The saved state
 */
export function getState(): MastersState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseJson<MastersState>(stored);
      if (parsed && parsed.breadcrumbs) {
        return {
          breadcrumbs: [...parsed.breadcrumbs],
        };
      }
    }
  } catch {
    // sessionStorage may not be available or data may be corrupted
  }
  return {
    breadcrumbs: [...DEFAULT_STATE.breadcrumbs],
  };
}

/**
 * Resets the saved state to default (root level).
 */
export function resetState(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage may not be available
  }
}

/**
 * Checks if there is a saved state other than the default.
 * @returns True if navigated into a collection
 */
export function hasSavedState(): boolean {
  return getState().breadcrumbs.length > 1;
}

/**
 * Fetches master/template assignment data.
 * Equivalent to MasterFactory.master() in assets/js/services.js
 * @param templateId - The template ID
 * @param collectionId - The collection ID
 * @returns Promise resolving to the master assignment response
 */
export async function getMasterAssignment(templateId: number, collectionId: string): Promise<MasterAssignmentResponse> {
  if (!templateId || collectionId.trim() === '') {
    throw new Error('templateId and collectionId are required');
  }

  const url = `${API_SERVER}/experience_templates/${templateId}/collection/${collectionId}/assignment`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch master assignment: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches master/template assignment data using collection ID 0.
 * Equivalent to MasterFactory.master() in assets/js/services.js when no collection context exists.
 * @param templateId - The template ID
 * @returns Promise resolving to the master assignment response
 */
export async function getMasterAssignmentByTemplateId(templateId: number): Promise<MasterAssignmentResponse> {
  if (!templateId) {
    throw new Error('templateId is required');
  }

  const url = `${API_SERVER}/experience_templates/${templateId}/collection/0/assignment`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch master assignment: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export interface PublishExperienceParams {
  parents?: string[];
  teacher_user_id: number;
  catalog_item_id: number;
  template_id: number;
  name: string;
  section_name: string;
  starts_at: string;
  ends_at: string | null;
  description?: string;
  notification?: string;
  small_groups: number;
  is_guided?: boolean;
  preview: boolean;
  review: boolean;
  return_url: string | null;
  project_id?: number;
  class_id: number | null;
  students: string;
}

export interface PublishExperienceResponse {
  id: number;
  [key: string]: unknown;
}

export interface ClassStateResponse {
  id: number;
  state_label: string;
  state_code: string;
  created: Date;
  updated: Date;
  default: boolean;
}

interface RawClassStateResponse extends Omit<ClassStateResponse, 'created' | 'updated'> {
  created: string;
  updated: string;
}

/**
 * Publishes/creates an experience.
 * Equivalent to ExperienceSaveFactory.save() in assets/js/services.js
 * @param params - The experience parameters
 * @returns Promise resolving to the created experience
 */
export async function publishExperience(params: PublishExperienceParams): Promise<PublishExperienceResponse> {
  const url = `${API_SERVER}/experiences`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: stringifyJson(params),
  });

  if (!response.ok) {
    throw new Error(`Failed to publish experience: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches available class states.
 * Equivalent to CourseAPI.getStates() in assets/js/services/classes.js
 * @returns Promise resolving to the states list
 */
export async function getStates(): Promise<ClassStateResponse[]> {
  const url = `${API_SERVER}/classes/states`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch class states: ${response.status} ${response.statusText}`);
  }

  const payload = await response.json();
  const states = Array.isArray(payload) ? payload as RawClassStateResponse[] : [];
  return states.map((state) => ({
    ...state,
    created: new Date(state.created),
    updated: new Date(state.updated),
  }));
}

const MastersService = {
  saveState,
  getState,
  resetState,
  hasSavedState,
  getMasterAssignment,
  getMasterAssignmentByTemplateId,
  getStates,
  publishExperience,
};

export default MastersService;
